import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, Download, Tag } from "lucide-react";
import { MaskedLines, Reveal } from "../components/motion";
import { articles } from "../data/content";
import { usePageTitle } from "../lib/title";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  usePageTitle(article ? article.title : "Education");

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-44 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-4xl font-light text-forest">Article not found</h1>
        <Link
          to="/education"
          data-testid="article-not-found-back-link"
          className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-terra"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Education
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <section className="mx-auto max-w-3xl px-6 pb-16 pt-36 lg:pt-44">
        <Reveal>
          <Link
            to="/education"
            data-testid="article-back-link"
            className="group inline-flex items-center gap-2 text-sm font-medium text-ink/60 transition-colors hover:text-terra"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Education
          </Link>
        </Reveal>
        <div className="mt-10 flex items-center gap-5">
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
            <Tag className="h-3.5 w-3.5 text-gold" /> {article.category}
          </span>
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
            <Clock className="h-3.5 w-3.5 text-gold" /> {article.readTime}
          </span>
          {article.pdf && (
            <a
              href={article.pdf}
              download
              data-testid="article-download-pdf"
              className="group flex items-center gap-2 rounded-full border border-forest/25 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-forest transition-all duration-300 hover:border-gold hover:text-terra"
            >
              <Download className="h-3.5 w-3.5 text-gold transition-transform duration-300 group-hover:translate-y-0.5" />
              Download PDF
            </a>
          )}
        </div>
        <MaskedLines
          className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-forest sm:text-5xl"
          lines={[article.title]}
        />
        <Reveal delay={0.35}>
          <p className="mt-6 border-l-2 border-gold pl-6 font-serif text-xl font-light italic leading-relaxed text-ink/70">
            {article.excerpt}
          </p>
        </Reveal>
      </section>

      <article className="mx-auto max-w-3xl px-6 pb-24" data-testid="article-body">
        {article.content.map((section, i) => (
          <Reveal key={i} delay={0.03 * i}>
            <div className={`py-8 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`article-section-${i}`}>
              <h2 className="font-serif text-2xl font-medium tracking-tight text-forest sm:text-3xl">
                <span className="mr-4 font-mono text-xs tracking-[0.3em] text-terra">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {section.heading}
              </h2>
              <div className="mt-5 space-y-5">
                {section.body.map((p, j) => (
                  <p key={j} className="text-base leading-relaxed text-ink/75">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        {article.faq ? (
          <Reveal>
            <div className="border-t border-border py-8" data-testid="article-faq">
              <h2 className="font-serif text-2xl font-medium tracking-tight text-forest sm:text-3xl">
                Frequently asked questions
              </h2>
              <div className="mt-5 space-y-6">
                {article.faq.map((item, i) => (
                  <div key={i} data-testid={`article-faq-${i}`}>
                    <h3 className="font-serif text-xl font-medium tracking-tight text-forest">{item.q}</h3>
                    <p className="mt-2 text-base leading-relaxed text-ink/75">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ) : null}

        {article.links ? (
          <Reveal>
            <div className="flex flex-wrap gap-6 pt-4" data-testid="article-links">
              {article.links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`article-link-${link.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className="group inline-flex items-center gap-1.5 text-sm font-medium text-terra transition-colors duration-300 hover:text-forest"
                >
                  {link.label}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </Reveal>
        ) : null}

        {article.note ? (
          <Reveal>
            <p className="mt-8 text-xs italic leading-relaxed text-ink/45" data-testid="article-note">
              {article.note}
            </p>
          </Reveal>
        ) : null}

        <Reveal>
          <div className="mt-12 border border-border bg-sand/50 p-8 lg:p-10" data-testid="article-cta">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">Questions About Your Own Plan?</p>
            <h3 className="mt-4 font-serif text-2xl font-medium tracking-tight text-forest sm:text-3xl">
              Let's apply this to <em className="text-terra">your</em> situation.
            </h3>
            <Link
              to="/contact?type=consultation"
              data-testid="article-consultation-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
